import React, { useEffect } from 'react';

import { Badge, Group, Modal, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconEqual, IconX } from '@tabler/icons-react';
import KiloAlert from 'components/KiloAlert';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { CellProps } from 'react-table';
import { PayrollEventItem } from 'types';
import { capitalizeFirstLetter } from 'utils/formatters';

import { SessionValueBreakdown } from './DrawerStaffDetailPayoutReport';

interface IModalTotalSessionValueBreakdownProps {
  opened: boolean;
  handleClose: () => void;
  breakdownData?: SessionValueBreakdown;
}

type AttendanceStatus = 'CHECKED_IN' | 'LATE_CANCEL' | 'NO_SHOW';

const ATTENDANCE_STATUS_BADGES: Record<AttendanceStatus, { label: string; color: string }> = {
  CHECKED_IN: { label: 'Checked-in', color: 'green' },
  LATE_CANCEL: { label: 'Late Cancel', color: 'orange' },
  NO_SHOW: { label: 'No Show', color: 'red' },
};

const ONLY_CHECKED_IN_NOTE = 'Only athletes who checked in contributed their session value to this total.';
const LATE_CANCEL_OR_NO_SHOW_NOTE =
  "Athletes who checked in contribute their session value to the total. Late cancels and no shows appear because this pay rate includes them and the athlete's credit was deducted.";

const getAttendanceStatus = (item: PayrollEventItem): AttendanceStatus => {
  if (item.attendanceStatus === 'LATE_CANCEL') return 'LATE_CANCEL';
  if (item.attendanceStatus === 'NO_SHOW') return 'NO_SHOW';
  return 'CHECKED_IN';
};

const countByStatus = (items: PayrollEventItem[], status: AttendanceStatus): number =>
  items.filter(item => getAttendanceStatus(item) === status).length;

const getNote = (items: PayrollEventItem[]): string => {
  const hasLateCancelOrNoShow = items.some(item => getAttendanceStatus(item) !== 'CHECKED_IN');
  if (hasLateCancelOrNoShow) return LATE_CANCEL_OR_NO_SHOW_NOTE;
  return ONLY_CHECKED_IN_NOTE;
};

const ModalTotalSessionValueBreakdown = ({
  opened,
  handleClose,
  breakdownData,
}: IModalTotalSessionValueBreakdownProps): JSX.Element => {
  const { formatCurrency } = useLocalization();
  const { trackTotalSessionValueBreakdownViewed } = useStaffTracking();

  const items = breakdownData?.payrollEventItems ?? [];
  const percentage = breakdownData?.payrollEventItems[0].payRatePercentage || 0;
  const totalSessionValue = items.reduce((sum, item) => sum + item.sessionValue, 0);

  useEffect(() => {
    if (!opened || !breakdownData) return;
    trackTotalSessionValueBreakdownViewed({
      pay_rate_type: 'percentage',
      athletes_total: items.length,
      athletes_checked_in: countByStatus(items, 'CHECKED_IN'),
      athletes_late_cancel: countByStatus(items, 'LATE_CANCEL'),
      athletes_no_show: countByStatus(items, 'NO_SHOW'),
      total_session_value: totalSessionValue,
      payout_amount: Math.round(totalSessionValue * percentage),
    });
    // Fire once per open, not on every re-render of the same breakdown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, breakdownData]);

  const columns = [
    {
      Header: 'Athlete',
      accessor: 'athleteFullName' as const,
    },
    {
      Header: 'Status',
      accessor: 'attendanceStatus' as const,
      Cell: ({ row }: CellProps<PayrollEventItem>) => {
        const badge = ATTENDANCE_STATUS_BADGES[getAttendanceStatus(row.original)];
        return (
          <Badge variant='light' color={badge.color}>
            {badge.label}
          </Badge>
        );
      },
    },
    {
      Header: 'Package',
      accessor: 'packageName' as const,
    },
    {
      Header: 'Billing Plan Name',
      accessor: 'billingPlanName' as const,
    },
    {
      Header: 'Billing Plan Price',
      accessor: 'billingPlanPrice' as const,
      Cell: ({ value }: CellProps<PayrollEventItem>) => (
        <Text align='center'>{formatCurrency(value / 100)}</Text>
      ),
    },
    {
      Header: 'Session Value',
      accessor: 'sessionValue' as const,
      Cell: ({ value }: CellProps<PayrollEventItem>) => (
        <Text align='center'>{formatCurrency(value / 100)}</Text>
      ),
    },
  ];

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleClose}
      title={<Title order={3}>Payout Breakdown</Title>}
      size='xl'
    >
      <Stack spacing='0'>
        <Group position='apart' align='flex-start'>
          <Stack spacing={12}>
            <Text fw='bold' size='xl' color='dimmed'>
              {breakdownData?.genesisEventName}
            </Text>
            <Badge variant='light' size='md' w='fit-content' color='gray'>
              {capitalizeFirstLetter(breakdownData?.genesisEventType ?? '')}
            </Badge>
          </Stack>
          <Group spacing={'xs'}>
            <Group bg={'gray.2'} p={'md'} sx={{ borderRadius: '0.5rem' }} align='center'>
              <Stack spacing={0} align='center'>
                <Text size='xs' color='dimmed'>
                  Session Value
                </Text>
                <Text size='lg' fw='bold' color='#495057'>
                  {formatCurrency(totalSessionValue / 100)}
                </Text>
              </Stack>
              <IconX />
              <Stack spacing={0} align='center'>
                <Text size='xs' color='dimmed'>
                  Pay Rate
                </Text>
                <Text size='lg' fw='bold' color='#495057'>
                  {percentage * 100}%
                </Text>
              </Stack>
              <IconEqual />
            </Group>
            <Stack spacing={0} align='center' bg={'gray.2'} p={'xs'} sx={{ borderRadius: '0.5rem' }}>
              <Text size='xs' color='dimmed'>
                Payout
              </Text>
              <Text size='1.75rem' fw='bold' color='green.8' lh={1.4}>
                {formatCurrency((totalSessionValue * percentage) / 100)}
              </Text>
            </Stack>
          </Group>
        </Group>

        <Stack spacing={0}>
          <Group position='apart'>
            <Text fw='bold' color='dimmed'>
              Athletes ({items.length})
            </Text>
          </Group>

          <Table tableData={items} columns={columns} isSuccess isLoading={false} isError={false} />

          <Group sx={{ justifyContent: 'flex-end' }}>
            <Text color='#444444'>Total Session Value</Text>
            <Text color='#444444'>{formatCurrency(totalSessionValue / 100)}</Text>
          </Group>
        </Stack>

        <KiloAlert icon={<IconAlertTriangle size={16} />} mt='2rem !important'>
          {getNote(items)}
        </KiloAlert>
      </Stack>
    </Modal>
  );
};

export default ModalTotalSessionValueBreakdown;
